/**
 * Analytics — PostHog event instrumentation.
 *
 * Call initAnalytics() once at startup (main.tsx).
 * Instrument key conversion and retention events via the typed helpers below.
 *
 * In local dev (no VITE_POSTHOG_KEY) all calls are no-ops — no console noise.
 *
 * Privacy rule: events count what happened, never what someone feels or
 * writes. No emotion, intensity, story text or journal content goes to
 * PostHog, and nothing is stored on the device for analytics. The public
 * privacy policy promises both; the tests in src/test/analytics.test.ts
 * hold them.
 */
import posthog from 'posthog-js'

let _initialised = false

export function initAnalytics(
  apiKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined,
  host = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://app.posthog.com',
): void {
  if (!apiKey) return
  posthog.init(apiKey, {
    api_host: host,
    // Don't auto-capture every click/page — we track intentional events only
    autocapture: false,
    capture_pageview: true,
    // No cookie or localStorage identifier: usage is counted per page load.
    persistence: 'memory',
  })
  _initialised = true
}

function track(event: string, properties?: Record<string, unknown>): void {
  if (!_initialised) return
  posthog.capture(event, properties)
}

// ── Typed event helpers ───────────────────────────────────────────────────────

export function trackSessionStarted(): void {
  track('session_started')
}

export function trackSessionCompleted(phase: number): void {
  track('session_completed', { phase_reached: phase })
}

export function trackSeriesSessionStarted(seriesId: string, position: number): void {
  track('series_session_started', { series_id: seriesId, session_position: position })
}

export function trackUpgradePromptShown(tier: 'growth' | 'premium', location: string): void {
  track('upgrade_prompt_shown', { tier, location })
}

export function trackUpgradeCompleted(tier: 'growth' | 'premium', interval: 'month' | 'year'): void {
  track('upgrade_completed', { tier, interval })
}

export { posthog }
